import { Fragment, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useSuspenseQuery } from '@tanstack/react-query'

import { ChargingSessionStatusScreen, ChargingPointStatusScreen } from './screens'
import { ChargingPointScreenProps } from './screens/types'

import { ChargingPointStatus } from '@/types/ChargingPoint'
import { chargingPointStatusUtils } from '@/utils/chargingPointStatus'
import { createChargingPointQueryOptions } from '@/api/charging-points/options'
import {
  ChargingSessionInAppNotificationStatus,
  isAwaitingExternalPayment,
} from '@/lib/in-app-notifications/categories/charging_session'
import { useActiveSession } from '@/contexts/ActiveSessionContext'

import { ChargingSessionSummaryModal } from './modals/ChargingSessionSummaryModal'
import { ChargingSessionFailedModal } from './modals/ChargingSessionFailedModal'
import { StartChargingModal } from '../Station/StartChargingModal/StartChargingModal'
import { useDisclosure } from '@/hooks/useDisclosure'
import { useStartCharging } from '../Station/StartChargingModal/hooks/useStartCharging/useStartCharging'
import { useQueryPaymentCreditCards, useQueryPaymentCredits } from '@/api/payment/queries'
import { ChargingStopModal } from './modals/ChargingStopModal'
import { useStopCharging } from '../Station/StartChargingModal/hooks/useStartCharging/useStopCharging'
import { ChargingSummary } from '@/types/Charging'

const ChargingPointScreen = () => {
  const startChargingModal = useDisclosure()
  const chargingStopModal = useDisclosure()
  const chargingSummaryModal = useDisclosure<ChargingSummary>()
  const chargingFailedModal = useDisclosure()

  const params = useParams()
  const rawCpId = Number(params.chargingPointId)
  const cpId = isNaN(rawCpId) ? undefined : rawCpId

  const activeSession = useActiveSession()

  const sessionData = activeSession.notification?.data?.payload
  const notificationStatus = activeSession.notification?.data?.payload?.status

  const chargingPointQuery = useSuspenseQuery({
    ...createChargingPointQueryOptions({
      chargingPointId: cpId || 0,
      stationId: Number(params.stationId),
    }),
    refetchInterval: sessionData ? false : 5000,
  })

  const { data: paymentCredits } = useQueryPaymentCredits({ enabled: true })
  const { data: creditCards } = useQueryPaymentCreditCards(
    { infinite: true, charging_point_id: cpId || undefined },
    { enabled: startChargingModal.isOpen }
  )

  const startChargingMethods = useStartCharging({
    startChargingModal,
    chargingPoint: chargingPointQuery.data,
    paymentCredits,
    creditCards,
    notificationPayload: sessionData,
  })

  const stopChargingMethods = useStopCharging({
    chargingPoint: chargingPointQuery.data,
    onSuccess: chargingStopModal.close,
  })

  const screenProps: ChargingPointScreenProps = {
    requiresConnectionToStart: Boolean(chargingPointQuery.data?.connector_notifications),
    isStartingCharging: startChargingMethods.isStartingCharging,
    onStartCharging: startChargingMethods.onStartCharging,
    onStopCharging: chargingStopModal.open,
  }

  useEffect(() => {
    const shouldOpenChargingSummary =
      notificationStatus === ChargingSessionInAppNotificationStatus.FINISHED && !chargingSummaryModal?.isOpen

    const shouldOpenChargingFailed =
      notificationStatus === ChargingSessionInAppNotificationStatus.FAILED && !chargingFailedModal?.isOpen

    const shouldOpenPaymentModal = isAwaitingExternalPayment(sessionData)

    if (shouldOpenChargingSummary && sessionData) {
      chargingSummaryModal.open(sessionData)
      return
    }

    if (shouldOpenChargingFailed) {
      chargingFailedModal.open()
      return
    }

    if (shouldOpenPaymentModal) {
      startChargingModal.open()
    }
  }, [notificationStatus])

  const renderScreenByChargingPointStatus = (chargingPointStatus: ChargingPointStatus) => {
    if (chargingPointStatus === ChargingPointStatus.PREPARING) {
      return <ChargingPointStatusScreen.ConnectedChargingPointScreen {...screenProps} />
    }

    switch (chargingPointStatusUtils.getBucket(chargingPointStatus)) {
      case 'AVAILABLE':
        return <ChargingPointStatusScreen.AvailableChargingPointScreen {...screenProps} />
      case 'OCCUPIED':
        return <ChargingPointStatusScreen.OccupiedChargingPointScreen {...screenProps} />
      case 'MAINTENANCE':
        return <ChargingPointStatusScreen.MaintenanceChargingPointScreen {...screenProps} />
      case 'FAULTED':
        return <ChargingPointStatusScreen.FaultedChargingPointScreen {...screenProps} />
      case 'UNAVAILABLE':
        return <ChargingPointStatusScreen.UnavailableChargingPointScreen {...screenProps} />
      default:
        return null
    }
  }

  const renderScreenByChargingSessionStatus = (chargingSessionStatus: ChargingSessionInAppNotificationStatus) => {
    switch (chargingSessionStatus) {
      case ChargingSessionInAppNotificationStatus.NOT_STARTED:
      case ChargingSessionInAppNotificationStatus.WAITING_PAYMENT:
      case ChargingSessionInAppNotificationStatus.READY_TO_START:
        return <ChargingSessionStatusScreen.StartingChargingSessionScreen {...screenProps} />
      case ChargingSessionInAppNotificationStatus.IN_PROGRESS:
        return <ChargingSessionStatusScreen.InProgressChargingSessionScreen {...screenProps} />
      case ChargingSessionInAppNotificationStatus.FAILED_PAYMENT:
      case ChargingSessionInAppNotificationStatus.FAILED:
      case ChargingSessionInAppNotificationStatus.FINISHED:
        return <ChargingPointStatusScreen.AvailableChargingPointScreen {...screenProps} />
      case ChargingSessionInAppNotificationStatus.FINISHING:
        return <ChargingSessionStatusScreen.FinishingChargingSessionScreen {...screenProps} />
      case ChargingSessionInAppNotificationStatus.IDLE:
        return <ChargingSessionStatusScreen.IdleChargingSessionScreen {...screenProps} />
      default:
        return null
    }
  }

  const renderScreen = () => {
    if (sessionData) {
      return renderScreenByChargingSessionStatus(sessionData.status)
    }
    return renderScreenByChargingPointStatus(chargingPointQuery.data.status)
  }

  return (
    <Fragment>
      {renderScreen()}
      <StartChargingModal modalMethods={startChargingModal} startChargingMethods={startChargingMethods} />
      <ChargingSessionSummaryModal
        modalMethods={chargingSummaryModal}
        modalTitle="Sessão finalizada"
        onClose={async () => {
          await activeSession.dismissSession()
        }}
      />
      <ChargingStopModal modalMethods={chargingStopModal} stopChargingMethods={stopChargingMethods} />
      <ChargingSessionFailedModal
        modalMethods={chargingFailedModal}
        payload={sessionData}
        onClose={async () => {
          if (startChargingModal?.isOpen) {
            startChargingModal.close()
          }
          await activeSession.dismissSession()
        }}
      />
    </Fragment>
  )
}

export default ChargingPointScreen
